import { School } from '@/shared/types/entities/School';
import { Filter, FilterItem, FilterType } from '@/shared/types/table';
import {
  computed,
  defineComponent,
  reactive,
  SetupContext,
} from '@vue/composition-api';
import {
  RadioFilter,
  DateFilter,
  NumberFilter,
  SelectFilter,
  TextFilter,
  CheckboxFilter,
} from '../filter';

export type ReactiveData = {
  isOpen: boolean;
  addedFilterItems: FilterItem<School>[];
  searchText: string;
  currentDialogType: FilterType | undefined; // undefinedの時はダイアログを閉じる
  buttonCoordinate: { x: number | null; y: number | null };
};

type UnknownFilter = Filter<School>;

export type FilterKey = keyof UnknownFilter;

export type HandleChangeFilter = <T>(
  key: FilterKey,
  value: T,
  isRangeProps?: boolean,
) => void;

export const getCheckLabel = (name: FilterKey) => {
  switch (name) {
    case 'downloaded':
      return 'ダウンロード済';
    case 'status':
      return '期間内';
    default:
      return '';
  }
};

const convertItemToText = (item: FilterItem<School>): string => {
  switch (item.type) {
    case 'select':
      return `${item.label}: ${
        item.options &&
        item.options.find((option) => option.value === item.value)?.label
      }`;
    case 'checkbox':
      if (item.options) {
        return `${item.label}: ${item.options[item.value as number]?.label}`;
      }
      return `${item.label}: ${getCheckLabel(item.name as FilterKey)}`;
    case 'text':
      return `${item.label}: ${item.value}`;
    case 'date':
      return `${item.label}: ${item.value || ''} ~ ${
        item.rangeProps?.value || ''
      }`;
    default:
      return item.rangeProps?.value
        ? `${item.label}: ${item.value} ~ ${item.rangeProps.value}`
        : `${item.label}: ${item.value}`;
  }
};

type Props = {
  filter: UnknownFilter;
  setFilter: (filter: UnknownFilter) => void;
  removeFilterItem: (key: string) => void;
  handleFetch: () => void;
  tabIndex?: number;
};

export default defineComponent({
  components: {
    RadioFilter,
    DateFilter,
    NumberFilter,
    SelectFilter,
    TextFilter,
    CheckboxFilter,
  },
  setup(_, ctx: SetupContext) {
    const props = {
      ...ctx.attrs,
      setFilter: ctx.attrs['set-filter'],
      removeFilterItem: ctx.attrs['remove-filter-item'],
      handleFetch: ctx.attrs['handle-fetch'],
      tabIndex: ctx.attrs['tab-index'],
    } as Props;

    const state = reactive<ReactiveData>({
      isOpen: false,
      addedFilterItems: [], // テーブル上部のフィルター表示
      searchText: '',
      currentDialogType: undefined,
      buttonCoordinate: { x: null, y: null },
    });

    const removeFilterItem = (key: string) => {
      const itemKey = key as keyof Filter<School>;
      props.filter[itemKey].value = undefined;
      if (props.filter[itemKey].rangeProps) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        props.filter[itemKey].rangeProps!.value = undefined;
      }
      state.addedFilterItems = state.addedFilterItems.filter(
        (item) => item.key !== key,
      );

      props.handleFetch();
    };

    const setFilter = (filter: Filter<School>) => {
      props.filter = filter;
    };

    const addedFilterItems = computed(() => {
      return state.addedFilterItems.reduce<FilterItem<School>[]>(
        (prev, item) => {
          if (item.value) {
            prev.push(item);
          }

          return prev;
        },
        [],
      );
    });

    const onOpen = (type: FilterType) => {
      state.currentDialogType = type;
    };

    const closeFilterMenu = () => {
      state.isOpen = false;
    };

    const onClose = () => {
      state.currentDialogType = undefined;
      closeFilterMenu();
    };

    const menuDialogStyle = computed(() => {
      return state.currentDialogType || !state.isOpen
        ? { display: 'none' }
        : {};
    });

    /**
     * フィルタを追加ボタンの位置を取得する
     */
    const idBase = 'filter-button';
    const buttonId = props.tabIndex ? `${idBase}-${props.tabIndex}` : idBase;

    const getButtonCoordinate = () => {
      const buttonDOM = document.querySelector(`#${buttonId}`);

      if (buttonDOM) {
        const { left, top } = buttonDOM.getBoundingClientRect();
        state.buttonCoordinate.x = left - 16;
        state.buttonCoordinate.y = top + 46;
        state.isOpen = true;
      }
    };

    const handleChange = <T extends string | boolean | undefined>(
      key: FilterKey,
      value: T,
      isRangeProps = false,
    ) => {
      if (isRangeProps) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        props.filter[key].rangeProps!.value = value;
      } else {
        props.filter[key].value = value;
      }
    };

    const clearFilter = () => {
      state.addedFilterItems.forEach((item) => {
        removeFilterItem(item.key);
      });
      state.addedFilterItems = [];
      props.handleFetch();
    };

    const applyFilter = (key: FilterKey) => {
      const index = state.addedFilterItems.findIndex((selectedItem) => {
        return selectedItem.key === key;
      });

      const filter = {
        ...props.filter[key],
        rangeProps: { ...props.filter[key].rangeProps },
      } as FilterItem<School>;

      const isInvalid = (value: any) =>
        value === undefined || value === null || value === false;

      const isEmpty =
        isInvalid(filter.value) && isInvalid(filter.rangeProps?.value);
      const isAdded = index !== -1;

      const isClear = isAdded && isEmpty;

      if (index === -1) {
        if (isEmpty) return;
        state.addedFilterItems.push(filter);
      } else if (isClear) {
        removeFilterItem(key);
      } else {
        state.addedFilterItems.splice(index, 1, filter);
      }

      props.handleFetch();
      onClose();
    };

    const checkDisabled = (item: FilterItem<School>) => {
      if (item.type === 'date') {
        if (item.value === undefined && item.rangeProps?.value === undefined) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    };

    return {
      state,
      buttonId,
      ...props,
      onOpen,
      closeFilterMenu,
      onClose,
      menuDialogStyle,
      applyFilter,
      clearFilter,
      setFilter,
      removeFilterItem,
      addedFilterItems,
      getButtonCoordinate,
      handleChange,
      convertItemToText,
      checkDisabled,
    };
  },
});
